import { Row, Col } from 'antd'
import cx from 'classnames'
import _ from 'lodash'
import { required } from 'redux-form-validators'
import { Button, Icon } from '@vzmi/shp_shared_compo'
import { PRODUCT_SHIP_TYPE } from '@vzmi/shp_shared_compo/constants/proposal'
import { Field, FormValueSelector } from '../../../../../../Common/forms'
import SingleLineText from '../../../../../Common/ReadOnlyComponents/SingleLineText/SingleLineText'
import {
  InputCountField,
  InputField,
  MenuSelectField
} from '../../../../../../Common/Form'
import { ATTRIBUTE_CONSTRAINTS_TYPE } from '../../../../../../../constants/proposalTypes'
import { ListingFieldConfig } from '../../../../../Common/ProposalForm/Shared/FieldConfig'
import listingModelStyles from '../../../index.css'
import styles from './index.css'
import {
  COLUMN as MODEL_TABLE_COLUMN,
  customSelectSpecValidation,
  getSpecCountLimit
} from '../../../ModelTable'
import { PRODUCT_KEY } from '../../../constants'
import {
  formatAll,
  showOnlyNumber,
  showNoLessThan,
  showUnderInputNumberLimit,
  showRoundedNumber
} from '../../../../../../../lib/formatter'
import {
  stockValidation
} from '../../../../../Common/ProposalForm/Shared/validation/validations'
import { getAttrMap, getFakeProductId } from '../../../helpers'
import useDebounceInput from '../../../../../../BatchProposal/hook/useDebounceInput'
import { getMaxSizeImagesFromDataMap } from '..'

const DEBOUNCE_TIME = 500

export const requiredValidation = required({ msg: '必填欄位' })

export const getSpecFieldsProps = (specNames, specDetailMapBySpec) => {
  const specFieldsProps = _.map(specNames, specName => {
    const { constraints, options = [] } = _.get(specDetailMapBySpec, specName, {})
    const fieldType = _.get(constraints, 'type')
    // 判斷欄位是選單或可以自訂的輸入匡
    const isCustomField = (
      // 自訂屬性
      _.isUndefined(constraints) ||
      // 文字匡
      fieldType === ATTRIBUTE_CONSTRAINTS_TYPE.TEXT
    )
    return { isCustomField, options }
  })
  return specFieldsProps
}

// 欄位值來自選單或輸入匡，額外判斷陣列則取第一個值
export const getCurrentValue = optionValue => {
  const currentValue = _.isArray(optionValue) ? _.first(optionValue) : optionValue
  return currentValue
}

// 使用賣場屬性調整的驗證，加一層轉換欄位
export const specValidate = limit => value => {
  return customSelectSpecValidation(value, limit)
}

export const getComponentAndFieldsByLayerField = currentModelField => (isCustomField, index) => {
  const component = isCustomField ? InputCountField : MenuSelectField
  let attrField
  let specSelectedValueField
  // 設定第一二層屬性 redux form 欄位名稱
  if (index === 0) {
    attrField = PRODUCT_KEY.ATTR
    specSelectedValueField = PRODUCT_KEY.SPEC_SELECTED_VALUE
  } else {
    attrField = PRODUCT_KEY.ITEM_ATTR
    specSelectedValueField = PRODUCT_KEY.ITEM_SPEC_SELECTED_VALUE
  }
  // 統一商品的屬性資料格式為陣列
  // 自訂欄位為輸入匡只寫入資料到 attr[0] = x
  // 非自訂欄位為選單寫入資料為 attr = [x]
  const attrFieldName = `${currentModelField}.${attrField}${isCustomField ? '[0]' : ''}`
  const specSelectedValueFieldName = `${currentModelField}.${specSelectedValueField}`
  return [component, attrFieldName, specSelectedValueFieldName]
}

const CustomCols = props => {
  const {
    change,
    submitFailed,
    deleteProduct,
    readOnly,
    currentModelField,
    listingName,
    modelSpecHeader,
    itemSpecHeader,
    isNoModelsListing,
    dataMap,
    specDetailMapBySpec = {},
    setImageTableKey
  } = props
  const rowIndex = _.trim(currentModelField, 'models[]')
  const isTwoLayerListing = !_.isEmpty(itemSpecHeader)
  const attrMap = getAttrMap(isTwoLayerListing)
  const [
    {
      isCustomField: isLayerOneCustomField,
      options: layerOneOptions
    },
    {
      isCustomField: isLayerTwoCustomField,
      options: layerTwoOptions
    }
  ] = getSpecFieldsProps([modelSpecHeader, itemSpecHeader], specDetailMapBySpec)
  const [
    [LayerOneSpecComponent, attrField, selectedValueField],
    [LayerTwoSpecComponent, itemAttrField, itemSelectedValueField]
  ] = [isLayerOneCustomField, isLayerTwoCustomField].map(getComponentAndFieldsByLayerField(currentModelField))
  const Component = readOnly ? SingleLineText : InputField

  const onLayerOneSpecNameChange = useDebounceInput((e, newValue, preValue, fieldName) => {
    if (!isTwoLayerListing) {
      return
    }

    const currentGroupedRows = _.get(dataMap, newValue, [])
    // 判斷選擇的第一層屬性是否為既有屬性
    // 表格列非 isNew 且大於 0 筆
    const isExistSpecSelectedValue = _.flow(
      () => _.filter(currentGroupedRows, row => !row[PRODUCT_KEY.IS_NEW]),
      filtedRows => (_.size(filtedRows) > 0)
    )()
    // 第一層改成已存在目前賣場的規格則取代掉圖片
    const newImages = getMaxSizeImagesFromDataMap(dataMap, newValue)
    // 設定商品圖片
    change(`${currentModelField}.${PRODUCT_KEY.NEW_PRODUCT_IMAGES}`, newImages)
    // 設定商品是否為圖片已共用
    change(`${currentModelField}.editable`, !isExistSpecSelectedValue)
    const newProductId = isExistSpecSelectedValue
      // 重設第一層 sku，如果已存在則用既有的 sku，這樣在批次修改第一層屬性時才找得到同一個第一層 sku 的雙層屬性
      ? _.get(currentGroupedRows, `0.${PRODUCT_KEY.ID}`)
      // 如果新的屬性名稱不是既有的則產生假的 id，送出時會過濾假的 id 以產生新的第一層商品
      : getFakeProductId(Date.now())
    change(`${currentModelField}.${PRODUCT_KEY.ID}`, newProductId)
    // 用於判斷新增商品的圖片是否為共用的圖片，共用圖片的話在送給 API 時要移除
    change(`${currentModelField}.${PRODUCT_KEY.IS_SHARED_IMAGES}`, _.size(newImages) > 1)
  }, DEBOUNCE_TIME)

  const onAttrChange = useDebounceInput((e, newValue, preValue, fieldName) => {
    const currentValue = getCurrentValue(newValue)
    // models[x].attr
    const isLayerOneAttrChange = _.includes(fieldName, PRODUCT_KEY.ATTR)
    const currentSelectedValueField = isLayerOneAttrChange
      ? selectedValueField
      : itemSelectedValueField
    // sync selectedValue field
    change(currentSelectedValueField, currentValue)
    // allow update image
    change(`${currentModelField}.isSpecSelected`, true)
    // 雙層屬性賣場第一層變更時會連動其圖片
    if (isTwoLayerListing && isLayerOneAttrChange) {
      onLayerOneSpecNameChange(e, currentValue)
      // 設定拖曳的分群
      change(`${currentModelField}.collection`, currentValue)
    }

    // 每次變更第一層會修改 imageTable key 強制 rerender 清除既有的圖片
    // 因為第一層屬性變更後要求商品圖需要重選
    if (isLayerOneAttrChange) {
      setImageTableKey(Date.now())
    }
  }, DEBOUNCE_TIME)

  return (
    <FormValueSelector
      selector={select => ({
        isUpdateMode: select('isUpdateMode'),
        modelsFromFormValues: select('models'),
        selectedValue: select(selectedValueField),
        itemSelectedValue: select(itemSelectedValueField)
      })}
    >
      {({ isUpdateMode, modelsFromFormValues, selectedValue }) => {
        // 第一層屬性選取後才能選第二層
        const isDisableLayerTwo = _.isUndefined(selectedValue)
        const tableRows = [
          // 單層賣場代表目前既有全部商品表格列
          // 雙層賣場代表同樣第一層屬性(同一分群)的全部商品表格列
          ..._.get(dataMap, getCurrentValue(selectedValue), []),
          // 跳出視窗中的商品表格列
          ...modelsFromFormValues
        ]
        const specCountLimit = getSpecCountLimit(listingName, tableRows, MODEL_TABLE_COLUMN.SPEC_SELECTED_VALUE)
        const itemSpecCountLimit = isTwoLayerListing
          ? getSpecCountLimit(listingName, tableRows, MODEL_TABLE_COLUMN.ITEM_SPEC_SELECTED_VALUE)
          : 0
        return (
          <>
            {!isNoModelsListing && (
              <Col
                span={readOnly ? 8 : 10}
                key='specValueCustomCol'
                className={cx(styles.customCol, styles.colOffset)}
              >
                <Row
                  gutter={[8, 32]}
                  type='flex'
                >
                  <Col span={12}>
                    <Field
                      name={attrField}
                      component={LayerOneSpecComponent}
                      popupClassName={styles.menu}
                      onChange={onAttrChange}
                      validate={requiredValidation}
                      placeholder='請選擇或輸入項目'
                      disabled={readOnly}
                      fullWidth
                      showErrorOnActive
                      mustShowFormError
                      absoluteError
                      showErrorAfterSubmitted
                      // input count props
                      countLimit={specCountLimit}
                      // menu select props
                      options={layerOneOptions}
                      enableFilter={false}
                    />
                  </Col>
                  <Col span={12}>
                    <Field
                      name={selectedValueField}
                      component={InputCountField}
                      countLimit={specCountLimit}
                      validate={specValidate(specCountLimit)}
                      onChange={onLayerOneSpecNameChange}
                      placeholder='請輸入項目顯示名稱'
                      disabled={readOnly}
                      fullWidth
                      showErrorOnActive
                      mustShowFormError
                      absoluteError
                      showErrorAfterSubmitted
                    />
                  </Col>
                  {isTwoLayerListing && (
                    <>
                      <Col span={12}>
                        <Field
                          name={itemAttrField}
                          component={LayerTwoSpecComponent}
                          popupClassName={styles.menu}
                          onChange={onAttrChange}
                          placeholder='請選擇或輸入項目'
                          disabled={isDisableLayerTwo || readOnly}
                          fullWidth
                          showErrorOnActive
                          mustShowFormError
                          absoluteError
                          showErrorAfterSubmitted
                          // input count props
                          countLimit={itemSpecCountLimit}
                          // menu select props
                          options={layerTwoOptions}
                          enableFilter={false}
                        />
                      </Col>
                      <Col span={12}>
                        <Field
                          name={itemSelectedValueField}
                          component={InputCountField}
                          countLimit={itemSpecCountLimit}
                          validate={specValidate(itemSpecCountLimit)}
                          placeholder='請輸入項目顯示名稱'
                          disabled={isDisableLayerTwo || readOnly}
                          fullWidth
                          showErrorOnActive
                          mustShowFormError
                          absoluteError
                          showErrorAfterSubmitted
                        />
                      </Col>
                    </>
                  )}
                </Row>
              </Col>
            )}
            <Col
              span={4}
              className={cx(styles.customCol, styles.colOffset)}
            >
              <Field
                name={`${currentModelField}.${attrMap.stock}`}
                component={Component}
                customDefaultValue='0'
                format={formatAll(
                  showOnlyNumber,
                  showRoundedNumber,
                  showNoLessThan(0),
                  showUnderInputNumberLimit
                )}
                validate={stockValidation}
                // stock 後端只吃數字，所以在輸入時直接轉換型態
                parse={showOnlyNumber}
                placeholder='輸入備貨數量'
                fullWidth
                showErrorOnActive
                mustShowFormError={submitFailed}
                absoluteError
              />
            </Col>
            <Col
              span={5}
              key='barcodeAndPartNo'
              className={styles.customCol}
            >
              <FormValueSelector
                selector={select => ({
                  isExpress24HR: (select('skuDetail.shipType.id') === PRODUCT_SHIP_TYPE.EXPRESS_24HR)
                })}
              >
                {selectFormValues => {
                  return (
                    <Row gutter={[0, 36]}>
                      <Col span={24}>
                        <Field
                          {...ListingFieldConfig.getField('model.barcodeOld')}
                          name={`${currentModelField}.${attrMap.barcode}`}
                          component={Component}
                          fullWidth
                          showErrorOnActive
                          absoluteError
                        />
                      </Col>
                      <Col span={24}>
                        {
                          !isNoModelsListing && (
                            <Field
                              {...ListingFieldConfig.getField('model.partNo')}
                              name={`${currentModelField}.${attrMap.productPartNo}`}
                              className={cx({ [styles.partNo]: readOnly })}
                              component={readOnly ? SingleLineText : InputCountField}
                              fullWidth
                              showErrorOnActive
                              mustShowFormError
                              absoluteError
                            />
                          )
                        }
                      </Col>
                    </Row>
                  )
                }}
              </FormValueSelector>
            </Col>
            {!readOnly && (
              <Col span={1}>
                {!isUpdateMode && (
                  <Button
                    buttonType='outline'
                    size='small'
                    className={cx(
                      listingModelStyles.actionBtn,
                      listingModelStyles.deleteBtn,
                      listingModelStyles.visible,
                      listingModelStyles.alwaysVisible,
                      styles.deleteBtn
                    )}
                    onClick={deleteProduct(rowIndex, modelsFromFormValues)}
                  >
                    <Icon
                      name='delete'
                      color='#979ba7'
                      align='middle'
                      size={16}
                    />
                  </Button>
                )}
              </Col>
            )}
          </>
        )
      }}
    </FormValueSelector>
  )
}

export default CustomCols
